import React, { useCallback, useEffect, useMemo, useState } from 'react'
import ComboBox from '../../../components/ComboBox'
import { leagueOptions, sortingOptions, timeWindowOptions } from '../constants/mockLeagueEvents'
import leagueSelectionService from '../../../services/leagueSelectionService'

const FALLBACK_REGION = 'N/A'

const FiltersPanel = ({ filters, onFiltersChange, onReset, events }) => {
  const [manualLeagues, setManualLeagues] = useState([])
  const [manualSelectedLeagueId, setManualSelectedLeagueId] = useState('')
  const [manualIsLoading, setManualIsLoading] = useState(false)
  const [manualError, setManualError] = useState('')

  const { manualLeagueOptions, manualLeagueMap } = useMemo(() => {
    if (!manualLeagues.length) {
      return {
        manualLeagueOptions: [],
        manualLeagueMap: new Map(),
      }
    }

    const map = new Map()
    const options = manualLeagues
      .map((league) => {
        if (!league) return null
        const candidateId = league.leagueId ?? league.rapidLeagueId
        if (!candidateId) return null

        const normalisedId = String(candidateId)
        map.set(normalisedId, league)

        return {
          id: normalisedId,
          label: league.leagueName ?? `League ${candidateId}`,
          description: `League ID: ${candidateId}`,
          region: league.region ?? league.country ?? league.countryName ?? FALLBACK_REGION,
        }
      })
      .filter(Boolean)

    return {
      manualLeagueOptions: options,
      manualLeagueMap: map,
    }
  }, [manualLeagues])

  const manualSelectedOption = useMemo(() => {
    if (!manualSelectedLeagueId) return null
    return manualLeagueOptions.find((option) => option.id === manualSelectedLeagueId) ?? null
  }, [manualSelectedLeagueId, manualLeagueOptions])

  const manualSelectedEvents = useMemo(() => {
    const league = manualLeagueMap.get(manualSelectedLeagueId)
    const details = league?.eventDetails
    if (!Array.isArray(details)) {
      return []
    }
    return details
  }, [manualLeagueMap, manualSelectedLeagueId])

  const leagueStats = useMemo(() => {
    const grouped = events.reduce((acc, event) => {
      if (!acc[event.leagueId]) {
        acc[event.leagueId] = {
          count: 0,
          name: event.leagueName,
          region: event.region ?? FALLBACK_REGION,
        }
      }
      acc[event.leagueId].count += 1
      return acc
    }, {})

    return Object.entries(grouped)
      .map(([id, meta]) => ({
        id,
        label: meta.name,
        region: meta.region,
        count: meta.count,
      }))
      .sort((a, b) => b.count - a.count)
  }, [events])

  const leagueSearchOptions = useMemo(() => leagueOptions, [])

  const activeLeague = useMemo(() => {
    if (!filters.leagueId) return null
    return leagueSearchOptions.find((option) => option.id === filters.leagueId)
  }, [filters.leagueId, leagueSearchOptions])

  const loadManualLeagues = useCallback(async () => {
    setManualIsLoading(true)
    setManualError('')
    try {
      const response = await leagueSelectionService.getLeagues()
      const payload = response?.data?.leagueData ?? response?.data ?? []
      if (!Array.isArray(payload)) {
        setManualLeagues([])
        return
      }
      setManualLeagues(payload)
    } catch (error) {
      console.error('Failed to fetch manual league list', error)
      setManualError('Unable to fetch leagues right now. Please try again shortly.')
      setManualLeagues([])
    } finally {
      setManualIsLoading(false)
    }
  }, [])

  useEffect(() => {
    loadManualLeagues()
  }, [loadManualLeagues])

  useEffect(() => {
    if (!manualSelectedLeagueId || !manualLeagueOptions.length) {
      return
    }
    const exists = manualLeagueOptions.some((option) => option.id === manualSelectedLeagueId)
    if (!exists) {
      setManualSelectedLeagueId('')
    }
  }, [manualLeagueOptions, manualSelectedLeagueId])

  const handleSearchChange = (event) => {
    onFiltersChange({
      search: event.target.value,
    })
  }

  const handleLeagueChange = (option) => {
    onFiltersChange({
      leagueId: option?.id ?? '',
    })
  }

  const handleTimeWindowChange = (windowOption) => {
    onFiltersChange({
      timeWindow: windowOption,
    })
  }

  const handleSortChange = (sortOption) => {
    onFiltersChange({
      sortOption,
    })
  }

  const handleResetClick = () => {
    onReset()
    setManualSelectedLeagueId('')
  }

  const handleManualSelect = (option) => {
    setManualSelectedLeagueId(option?.id ?? '')
  }

  const filterManualOptions = useCallback((options, currentQuery) => {
    const limit = 60
    if (!currentQuery.trim()) {
      return options.slice(0, limit)
    }
    const lowered = currentQuery.toLowerCase()
    const matches = []
    for (let index = 0; index < options.length; index += 1) {
      const option = options[index]
      const label = option.__label?.toLowerCase() ?? ''
      const description = option.__description?.toLowerCase() ?? ''
      const region = option.region?.toLowerCase() ?? ''
      const identifier = option.id?.toLowerCase() ?? ''

      if (
        label.includes(lowered) ||
        description.includes(lowered) ||
        region.includes(lowered) ||
        identifier.includes(lowered)
      ) {
        matches.push(option)
        if (matches.length >= limit) {
          break
        }
      }
    }
    return matches
  }, [])

  const formatManualEventTime = (event) => {
    const source = event?.eventTime ?? event?.eventDate
    if (!source) return 'TBD'
    const date = new Date(source)
    if (Number.isNaN(date.valueOf())) {
      return 'TBD'
    }
    return date.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
  }

  return (
    <section className="rounded-2xl border border-customAccent/20 bg-gradient-to-br from-white via-customSlate/50 to-white p-4 shadow-xl shadow-customAccent/10 dark:border-customMuted/30 dark:from-custom_dark_background dark:via-customSurface dark:to-custom_dark_primary2">
      <div className="flex flex-col gap-4 lg:grid lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] lg:gap-6">
        <div className="flex flex-col gap-4">
          {/* <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold uppercase tracking-wide text-customMuted">
                League quick search
              </label>
              <div className="flex items-center gap-2">
                <div className="grow">
                  <ComboBox
                    options={leagueSearchOptions}
                    value={activeLeague}
                    onChange={handleLeagueChange}
                    placeholder="Search league by name"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleResetClick}
                  className="rounded-xl border border-customAccent/40 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-customAccent transition hover:bg-customAccent/10 focus:outline-none focus:ring-2 focus:ring-customAccent/30 dark:border-customAccent/50 dark:text-customHeading dark:hover:bg-customAccent/10"
                >
                  Reset
                </button>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold uppercase tracking-wide text-customMuted">
                Search teams or keywords
              </label>
              <input
                type="search"
                value={filters.search}
                onChange={handleSearchChange}
                placeholder="Ex: Arsenal, Asian Handicap"
                className="rounded-xl border border-customAccent/20 bg-white px-4 py-2 text-sm text-customPrimaryText shadow-sm transition focus:border-customAccent focus:outline-none focus:ring-2 focus:ring-customAccent/40 dark:border-customSurfaceMuted dark:bg-customSurface dark:text-customHeading"
              />
            </div>
          </div> */}

          <div className="rounded-xl border border-customAccent/10 bg-white/70 p-3 shadow-inner shadow-customAccent/10 backdrop-blur-sm dark:border-customSurfaceMuted dark:bg-customSurfaceMuted/60">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-customMuted">Advanced filters</p>
            <div className="grid gap-4 md:grid-cols-1">
              <div className="flex flex-col gap-2">
                <span className="text-xs font-medium text-customSecondaryText dark:text-customMuted">
                  Time horizon
                </span>
                <div className="flex flex-wrap gap-2">
                  {timeWindowOptions.map((option) => {
                    const isActive = option.id === filters.timeWindow?.id
                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => handleTimeWindowChange(option)}
                        className={`rounded-full px-4 py-2 text-xs font-semibold transition ${
                          isActive
                            ? 'bg-customAccentBlue text-white shadow-md shadow-customAccentBlue/40'
                            : 'bg-customSlate text-customSecondaryText hover:bg-customAccentBlue/10 hover:text-customAccentBlue dark:bg-customSurface dark:text-customMuted dark:hover:bg-customAccent/10'
                        }`}
                      >
                        {option.label}
                      </button>
                    )
                  })}
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <span className="text-xs font-medium text-customSecondaryText dark:text-customMuted">Event Sorting</span>
                <div className="flex flex-wrap gap-2">
                  {sortingOptions.map((option) => {
                    const isActive = option.id === filters.sortOption?.id
                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => handleSortChange(option)}
                        className={`rounded-full px-4 py-2 text-xs font-semibold transition ${
                          isActive
                            ? 'bg-customAccentBlue text-white shadow-md shadow-customAccentBlue/40'
                            : 'bg-customSlate text-customSecondaryText hover:bg-customAccentBlue/10 hover:text-customAccentBlue dark:bg-customSurface dark:text-customMuted dark:hover:bg-customAccent/10'
                        }`}
                      >
                        {option.label}
                      </button>
                    )
                  })}
                </div>
                <div className="mt-2 grid gap-2">
                  {/* {leagueStats.slice(0, 3).map((league) => (
                    <div
                      key={league.id}
                      className="flex items-center justify-between rounded-xl border border-transparent bg-customSlate px-3 py-2 text-left text-sm text-customPrimaryText transition dark:bg-customSurface dark:text-customHeading"
                    >
                      <span>
                        {league.label}{' '}
                        <span className="text-xs font-medium text-customSecondaryText dark:text-customMuted">
                        - {league.region} 
                        </span>
                      </span>
                      <span className="rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-customAccent shadow-sm dark:bg-customSurfaceMuted dark:text-customHeading">
                        {league.count}
                      </span>
                    </div>
                  ))} */}
                  <span className="text-xs font-medium text-customSecondaryText dark:text-customMuted">
                    Most Events Leagues
                  </span>
                  {leagueStats.slice(0, 3).map((league) => (
                    <button
                      key={league.id}
                      type="button"
                      onClick={() => {
                        onFiltersChange({ leagueId: league.id })
                        setManualSelectedLeagueId(league.id)
                      }}
                      className="flex items-center justify-between rounded-xl border border-transparent bg-customSlate px-3 py-2 text-left text-sm text-customPrimaryText transition hover:border-customAccent/40 hover:bg-customAccent/10 hover:text-customAccent dark:bg-customSurface dark:text-customHeading dark:hover:bg-customAccent/10"
                    >
                      <span>
                        {league.label}{' '}
                        <span className="text-xs font-medium text-customSecondaryText dark:text-customMuted">
                          {/* - {league.region} */}
                          - Football
                        </span>
                      </span>
                      <span className="rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-customAccent shadow-sm dark:bg-customSurfaceMuted dark:text-customHeading">
                        {league.count}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
              {/* <div className="flex flex-col gap-2">
                <span className="text-xs font-medium text-customSecondaryText dark:text-customMuted">Primary market</span>
                <div className="flex flex-wrap gap-2">
                  {marketOptions.map((option) => {
                    const isActive = option.id === filters.market?.id
                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => handleMarketChange(option)}
                        className={`rounded-full px-4 py-2 text-xs font-semibold transition ${
                          isActive
                            ? 'bg-customVibrantGreen text-white shadow-md shadow-customVibrantGreen/40'
                            : 'bg-customSlate text-customSecondaryText hover:bg-customVibrantGreen/10 hover:text-customVibrantGreen dark:bg-customSurface dark:text-customMuted dark:hover:bg-customVibrantGreen/10'
                        }`}
                      >
                        {option.label}
                      </button>
                    )
                  })}
                </div>
              </div> */}
            </div>
          </div>
        </div>

        <aside className="flex flex-col gap-4 rounded-2xl border border-dashed border-customAccent/30 bg-white/70 p-4 shadow-inner dark:border-customAccent/30 dark:bg-customSurfaceMuted/80">
          <div>
            <p className="text-sm font-semibold text-customPrimaryText dark:text-customHeading">
              Manual league fetch
            </p>
            <p className="text-xs text-customSecondaryText dark:text-customMuted">
              Use league IDs to pull targeted fixtures without leaving your workflow.
            </p>
          </div>
          <div className="col-span-full flex items-center justify-center rounded-3xl border border-dashed border-customMuted dark:border-custom_dark_secondary bg-white dark:bg-custom_dark_primary2 py-20 text-center text-customMuted">
      These features are coming soon in a future update!
    </div>
        {/* 
          <label className="flex flex-col gap-2 text-xs font-medium text-customSecondaryText dark:text-customMuted">
            League ID
            <ComboBox
              options={manualLeagueOptions}
              value={manualSelectedOption}
              onChange={handleManualSelect}
              placeholder={manualIsLoading ? 'Loading leagues...' : 'Select a league to preview'}
              disabled={manualIsLoading}
              filterOptions={filterManualOptions}
            />
          </label>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={loadManualLeagues}
              className="flex items-center gap-2 rounded-xl bg-customAccentBlue px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-customAccent/30 transition hover:bg-customAccentBlue/80 focus:outline-none focus:ring-2 focus:ring-customAccent focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-customAccent dark:text-customHeading"
              disabled={manualIsLoading}
            >
              {manualIsLoading ? (
                <span className="inline-flex items-center gap-2">
                  <span className="h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Fetching
                </span>
              ) : (
                'Refresh list'
              )}
            </button>
            <button
              type="button"
              onClick={handleResetClick}
              className="rounded-xl border border-customAccent/40 px-4 py-2 text-sm font-medium text-customAccent transition hover:bg-customAccent/10 focus:outline-none focus:ring-2 focus:ring-customAccent/30 dark:border-customAccent/50 dark:text-customHeading dark:hover:bg-customAccent/10"
            >
              Reset
            </button>
          </div>
          {manualError ? (
            <div className="rounded-xl border border-customOrange/40 bg-customOrange/10 px-3 py-2 text-xs text-customOrange dark:border-customOrange/60 dark:bg-customOrange/20 dark:text-customHeading">
              {manualError}
            </div>
          ) : null}
          <div className="rounded-xl border border-customAccent/10 bg-customSlate/60 p-3 text-sm shadow-inner dark:border-customSurfaceMuted dark:bg-customSurfaceMuted/60">
            {manualSelectedLeagueId ? (
              manualSelectedEvents.length ? (
                <ul className="grid gap-2">
                  {manualSelectedEvents.map((event) => (
                    <li
                      key={event.upcommingEventId ?? event.rapidEventId ?? `${manualSelectedLeagueId}-${event.eventTime}`}
                      className="rounded-lg border border-white/20 bg-white/80 p-3 text-customPrimaryText shadow-sm backdrop-blur-sm dark:border-customSurfaceMuted dark:bg-customSurface"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex flex-col">
                          <span className="text-sm font-semibold">
                            {event.homeTeam ?? 'TBD'} vs {event.awayTeam ?? 'TBD'}
                          </span>
                          <span className="text-[11px] text-customSecondaryText dark:text-customMuted">
                            Event ID: {event.upcommingEventId ?? event.rapidEventId ?? 'N/A'}
                          </span>
                        </div>
                        <span className="text-xs font-semibold text-customAccent dark:text-customHeading">
                          {formatManualEventTime(event)}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-customSecondaryText dark:text-customMuted">
                  No upcoming events available for this league.
                </p>
              )
            ) : (
              <p className="text-xs text-customSecondaryText dark:text-customMuted">
                Select a league above to preview its upcoming fixtures.
              </p>
            )}
          </div> /*}
          {/* <div className="mt-2 grid gap-2">
            {leagueStats.slice(0, 3).map((league) => (
              <button
                key={league.id}
                type="button"
                onClick={() => {
                  onFiltersChange({ leagueId: league.id })
                  setManualSelectedLeagueId(league.id)
                }}
                className="flex items-center justify-between rounded-xl border border-transparent bg-customSlate px-3 py-2 text-left text-sm text-customPrimaryText transition hover:border-customAccent/40 hover:bg-customAccent/10 hover:text-customAccent dark:bg-customSurface dark:text-customHeading dark:hover:bg-customAccent/10"
              >
                <span>
                  {league.label}{' '}
                  <span className="text-xs font-medium text-customSecondaryText dark:text-customMuted">
                    - {league.region}
                  </span>
                </span>
                <span className="rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-customAccent shadow-sm dark:bg-customSurfaceMuted dark:text-customHeading">
                  {league.count}
                </span>
              </button>
            ))}
          </div> */}
        </aside>
      </div>
    </section>
  )
}

export default FiltersPanel
